import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { authAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { ArrowLeft, Mail, Lock, KeyRound, Shield } from "lucide-react";
import { toast } from "sonner";

/**
 * Two-stage password reset via SMS OTP.
 *  Stage 1: user types email → backend texts a 6-digit code to phone on file
 *  Stage 2: user types code + new password → backend verifies + rotates the hash
 *
 * The backend returns a generic "if that email is in our system..." response
 * to prevent account enumeration, so we mirror that in the UI copy.
 */
const ForgotPasswordPage = () => {
    const navigate = useNavigate();
    const [stage, setStage] = useState("request"); // 'request' | 'verify'
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const handleRequest = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await authAPI.forgotPassword(email.trim().toLowerCase());
            toast.success("If that email is registered, a reset code was texted to the phone on file.");
            setStage("verify");
        } catch (err) {
            // Generic failure — backend always 200s for this endpoint
            toast.error(err.response?.data?.detail || "Something went wrong. Try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleReset = async (e) => {
        e.preventDefault();
        if (newPassword.length < 6) {
            toast.error("Password must be at least 6 characters.");
            return;
        }
        setLoading(true);
        try {
            await authAPI.resetPassword({
                email: email.trim().toLowerCase(),
                code: code.trim(),
                new_password: newPassword,
            });
            toast.success("Password reset. Please log in with your new password.");
            navigate("/login");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Invalid or expired reset code.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4">
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="w-full max-w-md"
            >
                <Link
                    to="/login"
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
                    data-testid="back-to-login-link"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back to login
                </Link>

                <Card>
                    <CardHeader className="text-center">
                        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                            {stage === "request" ? (
                                <Shield className="h-5 w-5 text-primary" />
                            ) : (
                                <KeyRound className="h-5 w-5 text-primary" />
                            )}
                        </div>
                        <CardTitle className="font-serif text-2xl">
                            {stage === "request" ? "Reset your password" : "Enter your code"}
                        </CardTitle>
                        <CardDescription>
                            {stage === "request"
                                ? "We'll text a 6-digit reset code to the phone number on your account."
                                : `We just texted a code to the phone on file for ${email}. It's good for 15 minutes.`}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {stage === "request" ? (
                            <form onSubmit={handleRequest} className="space-y-4" data-testid="forgot-request-form">
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="email"
                                            type="email"
                                            className="pl-10"
                                            placeholder="you@example.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            data-testid="forgot-email-input"
                                        />
                                    </div>
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full rounded-full"
                                    disabled={loading}
                                    data-testid="send-code-btn"
                                >
                                    {loading ? "Sending..." : "Send reset code"}
                                </Button>
                            </form>
                        ) : (
                            <form onSubmit={handleReset} className="space-y-4" data-testid="forgot-verify-form">
                                <div className="space-y-2">
                                    <Label htmlFor="code">6-digit code</Label>
                                    <Input
                                        id="code"
                                        inputMode="numeric"
                                        pattern="[0-9]{6}"
                                        maxLength={6}
                                        placeholder="123456"
                                        className="text-center font-mono text-lg tracking-widest"
                                        value={code}
                                        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                        required
                                        data-testid="verify-code-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="new-password">New password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="new-password"
                                            type="password"
                                            className="pl-10"
                                            placeholder="At least 6 characters"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            required
                                            minLength={6}
                                            data-testid="new-password-input"
                                        />
                                    </div>
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full rounded-full"
                                    disabled={loading || code.length !== 6}
                                    data-testid="reset-password-btn"
                                >
                                    {loading ? "Resetting..." : "Reset password"}
                                </Button>
                                <button
                                    type="button"
                                    onClick={() => setStage("request")}
                                    className="w-full text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                                    data-testid="resend-code-btn"
                                >
                                    Didn't get a code? Send another
                                </button>
                            </form>
                        )}
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    );
};

export default ForgotPasswordPage;
